// Banner global «Estamos actualizando SmartDx» (#1757 v2 · opción C del mockup
// `tools/mockups/smartdx_mockups_login-mantenimiento.html`, sección v-C).
// Spec: .claude/specs/1757-login-mantenimiento/ (R6, R8, R10, R11, R13, R19, R20).
//
// Una sola pieza para todo 502/503 de la app (el login incluido, que NO se toca). Se monta UNA vez
// en App.js, fuera de AlcanceOscuro: sigue el tema claro/oscuro de :root, no el scope oscuro del
// acceso. El estado vive en `helpers/mantenimiento.js`; acá solo se dibuja.
//
// No es un diálogo (R11): `position: fixed`, sin foco propio, sin `aria-modal`, y el contenedor
// completo deja pasar los eventos (`pointer-events: none`); solo la barra los recibe. El texto que el
// médico escribe en el Turno, el autosave y sus reintentos no pasan por acá.
//
// Colores: SOLO tokens de theme.js (R19). Tema claro: la barra es `--bg-elevated` (blanca) sobre la
// foto oscura del acceso y el mockup avisa que se ve «pegada encima». Se separa con un halo del token
// primario + sombra propia (`--color-primary-rgb`, `--shadow-rgb`), sin ningún color escrito a mano.
import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { Box, ButtonBase, CircularProgress } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import {
  getEstadoMantenimiento,
  reintentarAhora,
  suscribirMantenimiento,
} from "../helpers/mantenimiento";

const cuenta = (proximoEn, ahora) => {
  const seg = Math.max(0, Math.ceil((proximoEn - ahora) / 1000));
  return `0:${String(seg).padStart(2, "0")}`;
};

export default function BannerMantenimiento() {
  const { estado, proximoEn } = useSyncExternalStore(
    suscribirMantenimiento,
    getEstadoMantenimiento,
    getEstadoMantenimiento,
  );
  const { pathname } = useLocation();
  const [ahora, setAhora] = useState(() => Date.now());

  // La cuenta regresiva solo corre mientras se muestra (en `espera`).
  useEffect(() => {
    if (estado !== "espera") return undefined;
    setAhora(Date.now());
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [estado, proximoEn]);

  // Mientras el banner está a la vista (R11, F1 del revisor): publica su alto en `--banner-mantenimiento-h`
  // (variable de layout de :root, NO un token de tema: 0px por defecto en cada `var(--<nombre>, 0px)`) y App.js EMPUJA el contenido con él en vez de dejar el banner encima de
  // los controles; el Turno descuenta la misma variable de su alto y, como mide contra la ventana, se le avisa
  // con un `resize` al ponerla y al quitarla. El alto sale de medir la barra (cambia con el ancho y el estado:
  // 3-4 líneas en celular) y lleva 8 px de aire. Al irse se QUITAN las dos propiedades y cada lector cae a su valor por defecto. No son tokens de
  // theme.js a propósito (revisor de #1757 v2): applyThemeVars (cambio de tema) y AlcanceOscuro (el acceso, que
  // reescribe todos los de tokens.dark en su subárbol) las pisarían con su valor por defecto. Escritorio del acceso: 148 px, como el
  // `.scr.conban .col` del mockup (en celular la columna ya arranca a 422 px y el banner no la toca).
  const visible = estado !== "oculto";
  const barraRef = useRef(null);
  useLayoutEffect(() => {
    if (!visible) return undefined;
    const raiz = document.documentElement;
    const avisar = () => window.dispatchEvent(new Event("resize"));
    const publicar = () => {
      const alto = barraRef.current ? Math.ceil(barraRef.current.getBoundingClientRect().height) : 0;
      const nuevo = `${alto + 8}px`;
      if (raiz.style.getPropertyValue("--banner-mantenimiento-h") === nuevo) return;
      raiz.style.setProperty("--banner-mantenimiento-h", nuevo);
      avisar();
    };
    raiz.style.setProperty("--acceso-pt-mantenimiento", "148px");
    publicar();
    const obs = typeof ResizeObserver !== "undefined" && barraRef.current ? new ResizeObserver(publicar) : null;
    if (obs) obs.observe(barraRef.current);
    return () => {
      if (obs) obs.disconnect();
      raiz.style.removeProperty("--banner-mantenimiento-h");
      raiz.style.removeProperty("--acceso-pt-mantenimiento");
      avisar();
    };
  }, [visible]);

  // Accesibilidad (F2 del revisor): la barra visible NO es una región viva (`aria-live="off"`); si lo fuera,
  // la cuenta regresiva se re-anunciaría cada segundo y una región insertada ya con texto no se anuncia de
  // forma fiable. El anuncio sale de una región viva PERSISTENTE (siempre montada, oculta a la vista) que solo
  // cambia al pasar de estado: espera, probando, volvió. La cuenta va con `aria-hidden`.
  const anuncio =
    estado === "espera"
      ? "Estamos actualizando SmartDx. Vuelve a intentarlo en unos minutos; todo seguirá como siempre."
      : estado === "probando"
        ? "Probando de nuevo."
        : estado === "volvio"
          ? "SmartDx ya está de vuelta."
          : "";
  const regionViva = (
    <Box
      data-testid="mantenimiento-anuncio"
      aria-live="polite"
      aria-atomic="true"
      sx={{
        // fixed en 0,0 y no absolute: sin offsets cae en su posición estática, que en el acceso del celular
        // quedaba a x=405 y daba 16 px de scroll horizontal (medido a 390).
        position: "fixed",
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
      }}
    >
      {anuncio}
    </Box>
  );

  if (!visible) return regionViva;

  const volvio = estado === "volvio";
  const probando = estado === "probando";
  const rgb = volvio ? "var(--color-success-rgb)" : "var(--color-primary-rgb)";

  return (
    <>
    {regionViva}
    <Box
      data-testid="mantenimiento-contenedor"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 0,
        zIndex: 1450, // sobre diálogos (1300) y snackbars; bajo tooltips (1500)
        pointerEvents: "none",
      }}
    >
      <Box
        ref={barraRef}
        data-testid="mantenimiento-banner"
        data-estado={estado}
        role="status"
        aria-live="off"
        sx={{
          position: "absolute",
          pointerEvents: "auto",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          overflow: "hidden",
          color: "var(--text-primary)",
          borderRadius: "18px",
          border: `1px solid rgba(${rgb}, 0.55)`,
          backgroundColor: "rgba(var(--bg-elevated-rgb), 0.94)",
          // Halo del token + sombra: separan la barra de la foto oscura en tema claro.
          boxShadow: `0 0 0 4px rgba(${rgb}, 0.22), 0 18px 44px -12px rgba(var(--shadow-rgb), 0.55)`,
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            backgroundColor: `rgba(${rgb}, 0.1)`,
            pointerEvents: "none",
          },
          animation: "mantenimientoEntra 220ms cubic-bezier(0.22, 1, 0.36, 1)",
          "@keyframes mantenimientoEntra": {
            from: { opacity: 0, translate: "0 -8px" },
            to: { opacity: 1, translate: "0 0" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          // Celular (390): márgenes de 8 px, el pie baja a su propia línea.
          top: { xs: "66px", sm: "76px" },
          left: { xs: "8px", sm: "50%" },
          right: { xs: "8px", sm: "auto" },
          width: { xs: "auto", sm: "min(820px, calc(100vw - 32px))" },
          transform: { xs: "none", sm: "translateX(-50%)" },
          flexWrap: { xs: "wrap", sm: "nowrap" },
          padding: { xs: "12px", sm: "12px 14px 12px 16px" },
          rowGap: { xs: "10px", sm: 0 },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "relative",
            flex: "none",
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `rgba(${rgb}, ${volvio ? 0.2 : 0.25})`,
            color: volvio ? "var(--color-success)" : "var(--color-primary-soft)",
          }}
        >
          {volvio ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
        </Box>

        <Box
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            lineHeight: 1.45,
            "& b": { fontWeight: 700 },
            "& span": { color: "var(--text-secondary)" },
            // En celular el texto ocupa lo que queda junto al ícono; el pie baja entero.
            flexBasis: { xs: "calc(100% - 48px)", sm: "auto" },
          }}
        >
          {volvio ? (
            <>
              <b>SmartDx ya está de vuelta.</b>{" "}
              <span>{pathname === "/authentication" ? "Ya puedes ingresar." : "Puedes seguir donde quedaste."}</span>
            </>
          ) : (
            <>
              <b>Estamos actualizando SmartDx.</b>{" "}
              <span>
                Vuelve a intentarlo en unos minutos; todo seguirá como siempre. Lo que escribiste no se pierde.
              </span>
            </>
          )}
        </Box>

        <Box
          sx={{
            position: "relative",
            flex: "none",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: 12.5,
            color: "var(--text-muted)",
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
            paddingLeft: { xs: "46px", sm: 0 },
            boxSizing: "border-box",
          }}
        >
          {volvio && <span>Se cierra solo</span>}
          {probando && (
            <>
              <CircularProgress
                aria-hidden="true"
                size={15}
                thickness={5}
                sx={{ color: "var(--color-primary-soft)", "@media (prefers-reduced-motion: reduce)": { animationDuration: "0s" } }}
              />
              <span>Probando de nuevo…</span>
            </>
          )}
          {estado === "espera" && (
            <>
              <span>
                Probamos de nuevo en{" "}
                <span data-testid="mantenimiento-cuenta" aria-hidden="true">{cuenta(proximoEn, ahora)}</span>
              </span>
              <ButtonBase
                type="button"
                data-testid="mantenimiento-reintentar"
                onClick={reintentarAhora}
                sx={{
                  px: "14px",
                  py: "8px",
                  borderRadius: "999px",
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  color: "var(--text-on-primary)",
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-primary-soft)" },
                  "@media (pointer: coarse)": { minHeight: "44px" },
                }}
              >
                Intentar ahora
              </ButtonBase>
            </>
          )}
        </Box>
      </Box>
    </Box>
    </>
  );
}
